import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Clock, DollarSign, MapPin, ShieldCheck } from "lucide-react";
import { payrollApi } from "../../lib/api";
import { computeRowExceptions, PAY_MODEL_LABELS, money2, errMsg } from "./payrollHelpers";

const STATUS_STYLES = {
  READY: { label: "Ready", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  WARNING: { label: "Warning", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  NEEDS_REVIEW: { label: "Needs Review", cls: "bg-rose-50 text-rose-700 border-rose-200" },
  APPROVED: { label: "Approved", cls: "bg-blue-50 text-blue-700 border-blue-200" },
};

const EXCEPTION_META = {
  missing_clockout: { label: "Missing Clock-Outs", icon: Clock },
  missing_pay_rate: { label: "Missing Pay Rate", icon: DollarSign },
  commission_issue: { label: "Commission Issues", icon: AlertTriangle },
  unapproved_adjustment: { label: "Unapproved Adjustment", icon: ShieldCheck },
  gps_time_conflict: { label: "GPS/Time Conflict", icon: MapPin },
};

const STATUS_FILTERS = [
  { key: "all", label: "All" },
  { key: "needs_review", label: "Needs Review" },
  { key: "exceptions", label: "Exceptions" },
  { key: "ready", label: "Ready" },
  { key: "approved", label: "Approved" },
];

export default function PayrollReviewDashboard({ rows, adjustmentsByEmployee, invoiceByJobId, clockHours, period, canPayroll, onOpenDetail }) {
  const [statusFilter, setStatusFilter] = useState("all");
  const [exceptionType, setExceptionType] = useState(null);
  const [techFilter, setTechFilter] = useState("");
  const [payTypeFilter, setPayTypeFilter] = useState("");
  const [jobTypeFilter, setJobTypeFilter] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [approvedIds, setApprovedIds] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  // "Approved" here is a lightweight, session-scoped review marker — not a
  // persisted field — so it always resets when the period/date range changes.
  useEffect(() => { setApprovedIds(new Set()); setSelected(new Set()); }, [period?.id]);

  const canBulkApprove = canPayroll("add_adjustments") || canPayroll("edit_commissions") || canPayroll("edit_hours");
  const isLocked = period?.status === "LOCKED";

  const enriched = useMemo(() => rows.map((row) => {
    const adjustments = adjustmentsByEmployee[row.tech.id] || [];
    const { hard, soft } = computeRowExceptions(row, {
      invoiceByJobId, adjustments,
      missingClockOutByEmail: clockHours?.missingClockOutByEmail,
      missingClockOutByUserId: clockHours?.missingClockOutByUserId,
    });
    let status;
    if (hard.length > 0) status = "NEEDS_REVIEW";
    else if (approvedIds.has(row.tech.id)) status = "APPROVED";
    else if (soft.length > 0) status = "WARNING";
    else status = "READY";
    const jobTypes = new Set((row.jobLines || []).map((jl) => jl.type).filter(Boolean));
    return { row, hard, soft, status, jobTypes };
  }), [rows, adjustmentsByEmployee, invoiceByJobId, clockHours, approvedIds]);

  const exceptionCounts = useMemo(() => {
    const counts = {};
    Object.keys(EXCEPTION_META).forEach((k) => { counts[k] = 0; });
    enriched.forEach(({ hard, soft }) => {
      [...hard, ...soft].forEach((f) => { if (counts[f.key] !== undefined) counts[f.key] += 1; });
    });
    return counts;
  }, [enriched]);
  const totalExceptions = Object.values(exceptionCounts).reduce((s, n) => s + n, 0);

  const jobTypeOptions = useMemo(() => {
    const set = new Set();
    enriched.forEach((e) => e.jobTypes.forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [enriched]);

  const filtered = enriched.filter((e) => {
    if (statusFilter === "needs_review" && e.status !== "NEEDS_REVIEW") return false;
    if (statusFilter === "exceptions" && !(e.status === "NEEDS_REVIEW" || e.status === "WARNING")) return false;
    if (statusFilter === "ready" && e.status !== "READY") return false;
    if (statusFilter === "approved" && e.status !== "APPROVED") return false;
    if (exceptionType && !([...e.hard, ...e.soft].some((f) => f.key === exceptionType))) return false;
    if (techFilter && e.row.tech.id !== techFilter) return false;
    if (payTypeFilter && e.row.pay.model !== payTypeFilter) return false;
    if (jobTypeFilter && !e.jobTypes.has(jobTypeFilter)) return false;
    return true;
  });

  const toggleSelect = (techId) => setSelected((s) => {
    const next = new Set(s);
    if (next.has(techId)) next.delete(techId); else next.add(techId);
    return next;
  });
  const toggleSelectAll = () => setSelected((s) => (s.size === filtered.length ? new Set() : new Set(filtered.map((e) => e.row.tech.id))));

  const approveSelected = async () => {
    const targets = enriched.filter((e) => selected.has(e.row.tech.id));
    const blocked = targets.filter((e) => e.status === "NEEDS_REVIEW");
    const eligible = targets.filter((e) => e.status !== "NEEDS_REVIEW");
    if (eligible.length === 0) {
      toast.error("Everyone selected needs individual review first — nothing to approve.");
      return;
    }
    setBulkBusy(true);
    try {
      for (const e of eligible) {
        const pending = (adjustmentsByEmployee[e.row.tech.id] || []).filter((a) => !a.approvedBy);
        await Promise.all(pending.map((a) => payrollApi.updateAdjustment(a.id, { approve: true })));
      }
      setApprovedIds((prev) => new Set([...prev, ...eligible.map((e) => e.row.tech.id)]));
      setSelected(new Set());
      if (blocked.length > 0) {
        toast.warning(`Approved ${eligible.length}. Skipped ${blocked.length} that still need individual review.`);
      } else {
        toast.success(`Marked ${eligible.length} employee${eligible.length > 1 ? "s" : ""} as reviewed.`);
      }
    } catch (err) {
      toast.error(errMsg(err, "Bulk approve failed"));
    } finally {
      setBulkBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="payroll-review-dashboard">
      <div className="border border-slate-200 rounded-xl p-4 bg-white">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[13px] font-bold uppercase tracking-wide text-slate-700">Payroll Exceptions</div>
          <div className="text-[13px] font-extrabold text-rose-600" data-testid="exceptions-total-count">{totalExceptions} Exception{totalExceptions !== 1 ? "s" : ""}</div>
        </div>
        {totalExceptions === 0 ? (
          <div className="flex items-center gap-2 text-[13px] text-emerald-600 font-semibold py-2">
            <CheckCircle2 className="h-4 w-4" /> No exceptions found — everyone is clear for review.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
            {Object.entries(EXCEPTION_META).map(([key, meta]) => {
              const count = exceptionCounts[key] || 0;
              const Icon = meta.icon;
              const active = exceptionType === key;
              return (
                <button
                  key={key}
                  disabled={count === 0}
                  onClick={() => setExceptionType(active ? null : key)}
                  data-testid={`exception-chip-${key}`}
                  className={`text-left rounded-lg border px-3 py-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${active ? "border-rose-400 bg-rose-50" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                >
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500"><Icon className="h-3.5 w-3.5" />{meta.label}</div>
                  <div className="text-[18px] font-extrabold text-slate-800 mt-0.5">{count}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1" data-testid="review-status-filters">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              data-testid={`review-status-filter-${f.key}`}
              className={`h-8 px-3 rounded-md text-[12px] font-semibold transition-colors ${statusFilter === f.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-700"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <select value={techFilter} onChange={(e) => setTechFilter(e.target.value)} data-testid="review-filter-technician" className="h-8 px-2 rounded-md border border-slate-200 text-[12px] bg-white">
          <option value="">All Technicians</option>
          {rows.map((r) => <option key={r.tech.id} value={r.tech.id}>{r.tech.name}</option>)}
        </select>
        <select value={payTypeFilter} onChange={(e) => setPayTypeFilter(e.target.value)} data-testid="review-filter-pay-type" className="h-8 px-2 rounded-md border border-slate-200 text-[12px] bg-white">
          <option value="">All Pay Types</option>
          {Object.entries(PAY_MODEL_LABELS).map(([k, label]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <select value={jobTypeFilter} onChange={(e) => setJobTypeFilter(e.target.value)} data-testid="review-filter-job-type" className="h-8 px-2 rounded-md border border-slate-200 text-[12px] bg-white">
          <option value="">All Job Types</option>
          {jobTypeOptions.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 border-b border-slate-200 bg-slate-50/60">
          <label className="flex items-center gap-2 text-[12px] font-semibold text-slate-600">
            <input type="checkbox" checked={filtered.length > 0 && selected.size === filtered.length} onChange={toggleSelectAll} data-testid="review-select-all-checkbox" />
            {selected.size > 0 ? `${selected.size} selected` : "Select all"}
          </label>
          {canBulkApprove && (
            <button
              onClick={approveSelected}
              disabled={selected.size === 0 || bulkBusy || isLocked}
              data-testid="review-approve-selected-btn"
              className="h-8 px-3 rounded-md bg-blue-600 hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[12px] font-semibold"
            >
              {bulkBusy ? "Approving…" : "Approve Selected"}
            </button>
          )}
        </div>
        <div className="text-[10.5px] text-slate-400 px-3 py-1.5 border-b border-slate-100">
          Approving only marks existing entries as reviewed — it never changes any dollar amount, and employees flagged "Needs Review" are always skipped until handled individually.
        </div>
        <div className="divide-y divide-slate-100">
          {filtered.map(({ row, hard, soft, status }) => {
            const style = STATUS_STYLES[status];
            const flagText = [...hard, ...soft].map((f) => f.label).join(" · ");
            return (
              <div key={row.tech.id} className="flex items-start gap-3 px-3 py-2.5 hover:bg-slate-50/60" data-testid={`review-row-${row.tech.id}`}>
                <input type="checkbox" className="mt-1" checked={selected.has(row.tech.id)} onChange={() => toggleSelect(row.tech.id)} data-testid={`review-row-checkbox-${row.tech.id}`} />
                <button type="button" onClick={() => onOpenDetail(row)} className="flex-1 text-left" data-testid={`review-row-open-${row.tech.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-slate-800">{row.tech.name}</span>
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${style.cls}`} data-testid={`review-status-badge-${row.tech.id}`}>{style.label}</span>
                    <span className="text-[11px] text-slate-400">{PAY_MODEL_LABELS[row.pay.model] || row.pay.model}</span>
                  </div>
                  {flagText && <div className="text-[11.5px] text-slate-500 mt-0.5">{flagText}</div>}
                </button>
                <div className="text-[13px] font-semibold text-slate-700 tabular-nums">{money2(row.total)}</div>
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-8 text-center text-[12.5px] text-slate-400 italic">No employees match the selected filters.</div>
          )}
        </div>
      </div>
    </div>
  );
}
